<template>
  <div class="custom-field-header">
    <template v-if="customField">
      <slot :label="getCustomFieldLabel(customField)" :value="customField" />
      <v-btn icon size="x-small" @click="onCustomFieldSelect(null)">
        <v-icon size="small">mdi-close</v-icon>
      </v-btn>
    </template>
    <v-select
      v-else
      :items="customFieldItems"
      item-title="text"
      placeholder="Select custom field"
      hide-details
      class="custom-field-select"
      @update:model-value="onCustomFieldSelect"
    />
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import { mapMutations, mapState } from 'vuex';

import { ITextValue } from '@/types/common';
import { memberOldService } from '@/bundles/Members/factory/memberOldServiceFactory';
import { SourceTypeEnum } from '@/bundles/App/enums';
import { Nullable } from '@/utils/types';
import { SET_CUSTOM_FIELDS } from '@/store/modules/settings/settings-types';
import { StoreModulesNamesEnum } from '@/store/StoreModulesNamesEnum';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

export default defineComponent({
  name: 'CustomFieldSelect',

  props: {
    customField: {
      type: String,
      default: null,
    },
    customFieldItems: {
      type: Array as PropType<ITextValue<string>[]>,
      default: () => [] as ITextValue<string>[],
    },
    source: {
      type: String as PropType<SourceTypeEnum>,
      required: true,
    },
  },

  emits: ['change:selected'],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  computed: {
    ...mapState(StoreModulesNamesEnum.Settings, {
      settingsKey: (state) => state.settings_key
    })
  },

  methods: {
    ...mapMutations(StoreModulesNamesEnum.Settings, {
      setCustomFields: SET_CUSTOM_FIELDS
    }),
    getCustomFieldLabel (value: string): string {
      const item = this.customFieldItems.find((i: ITextValue<string>) => i.value === value);
      return item ? item.text : value;
    },
    async onCustomFieldSelect (value: Nullable<SourceTypeEnum>) {
      if (!this.source) {
        return;
      }

      try {
        await memberOldService.updateCustomCustomField(this.settingsKey, {
          source: this.source,
          value: value
        });

        this.$emit('change:selected', value);
        this.setCustomFields({ [this.source]: value });
      } catch (error: any) {
        this.addNotification({ ...error, status: 0 });
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.custom-field-header {
  display: flex;
  align-items: center;
  min-width: 150px;
}

.custom-field-select {
  font-weight: normal;
}
</style>
